import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { Edit2, DollarSign, Percent, Check, X, Zap } from 'lucide-react';
import { BOOST_LABELS, REASON_LABELS, statusBadge } from '../../../lib/pricing-labels';
import { useCan, Can } from '../../../lib/rbac';

interface SiteItem {
  site_item_id: string;
  site_id: string;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  available_quantity: number;
  sold_quantity: number;
  net_proceeds: number | null;
  sale_fee: number | null;
  shipping_cost: number | null;
  permalink: string | null;
}

interface BoostInfo {
  id: string;
  status: 'boosted' | 'opportunity' | 'not_apply' | 'not_boosted';
  description?: string;
}

interface WinnerInfo {
  item_id?: string;
  price?: number;
  currency_id?: string;
}

interface CompetitionRow {
  site_item_id: string;
  site_id: string;
  ptw_status: string | null;
  current_price: number | null;
  price_to_win: number | null;
  price_gap: number | null;
  price_gap_pct: number | null;
  currency_id: string | null;
  boosts: BoostInfo[] | null;
  reason: string[] | null;
  winner_info: WinnerInfo | null;
  sharing_first_place_count: number | null;
  synced_at: string;
}

interface BenchmarkRow {
  site_item_id: string;
  site_id: string;
  status: string | null;
  currency_id: string | null;
  suggested_price_amount: number | null;
  suggested_price_usd: number | null;
  lowest_price_amount: number | null;
  lowest_price_usd: number | null;
  current_price_usd: number | null;
  percent_difference: number | null;
  synced_at: string;
}

interface PricingTabProps {
  product: any;
  sellerId: number;
  onRefresh: () => void;
}

function marginColor(pct: number | null): string {
  if (pct == null) return 'var(--color-text-muted)';
  if (pct >= 30) return 'var(--color-success)';
  if (pct >= 15) return 'var(--color-warning)';
  return 'var(--color-error)';
}

export default function PricingTab({ product, sellerId, onRefresh }: PricingTabProps) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [editing, setEditing] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [compRows, setCompRows] = useState<CompetitionRow[]>([]);
  const [compLoading, setCompLoading] = useState(false);
  const [benchmarkRows, setBenchmarkRows] = useState<BenchmarkRow[]>([]);
  const [applyingSite, setApplyingSite] = useState<string | null>(null);

  const amazonPrice = product.amazon_price ?? product.item_amazon_price;

  useEffect(() => {
    if (!product?.item_id) return;
    let cancelled = false;
    setCompLoading(true);
    api
      .get(`/catalog/products/${product.item_id}/competition?seller_id=${sellerId}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.success) setCompRows(res.data as CompetitionRow[]);
      })
      .finally(() => {
        if (!cancelled) setCompLoading(false);
      });
    api
      .get(`/catalog/products/${product.item_id}/benchmarks?seller_id=${sellerId}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.success) setBenchmarkRows(res.data as BenchmarkRow[]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [product?.item_id, sellerId]);

  const compBySite = useMemo(() => {
    const m = new Map<string, CompetitionRow>();
    for (const row of compRows) m.set(row.site_id, row);
    return m;
  }, [compRows]);

  const benchBySite = useMemo(() => {
    const m = new Map<string, BenchmarkRow>();
    for (const row of benchmarkRows) m.set(row.site_id, row);
    return m;
  }, [benchmarkRows]);

  const handleApplySuggested = async (siteId: string, suggested: number) => {
    const ok = window.confirm(
      canMoney
        ? `¿Aplicar el precio sugerido en ${siteId}?\n\nNuevo net proceeds: $${suggested.toFixed(2)}`
        : `¿Aplicar el precio sugerido en ${siteId}?`
    );
    if (!ok) return;
    setApplyingSite(siteId);
    try {
      const res = await api.put(
        `/catalog/products/${product.item_id}/ml/price/site?seller_id=${sellerId}&site_id=${siteId}`,
        { price: suggested }
      );
      if (res?.success) {
        onRefresh();
      } else {
        window.alert('No se pudo aplicar: ' + (res?.error || 'error desconocido'));
      }
    } catch (e) {
      window.alert('Error: ' + (e instanceof Error ? e.message : 'desconocido'));
    } finally {
      setApplyingSite(null);
    }
  };

  const handleEditStart = () => {
    setPriceInput(String(product.price ?? ''));
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setPriceInput('');
  };

  const handleEditSave = async () => {
    const newPrice = parseFloat(priceInput);
    if (isNaN(newPrice) || newPrice <= 0) return;
    setSaving(true);
    try {
      const res = await api.put(`/catalog/products/${product.item_id}/ml/price?seller_id=${sellerId}`, { price: newPrice });
      if (res.success) {
        setEditing(false);
        onRefresh();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleEditSave();
    if (e.key === 'Escape') handleEditCancel();
  };

  return (
    <div className="space-y-6">
      {/* Current Prices */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <DollarSign size={16} />
          Precios Actuales
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* ML Price - editable */}
          <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[var(--color-text-muted)]">Precio ML (CBT Parent)</span>
              {!editing && (
                <Can cap="catalog.edit_price">
                  <button
                    onClick={handleEditStart}
                    className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                    title="Editar precio"
                  >
                    <Edit2 size={14} />
                  </button>
                </Can>
              )}
            </div>
            {editing ? (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-[var(--color-text)]">$</span>
                <input
                  type="number"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1 text-lg font-semibold rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                />
                <button
                  onClick={handleEditSave}
                  disabled={saving}
                  className="p-1.5 rounded bg-[var(--color-success)] text-white hover:opacity-80 disabled:opacity-50"
                  title="Guardar"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={handleEditCancel}
                  disabled={saving}
                  className="p-1.5 rounded bg-[var(--color-error)] text-white hover:opacity-80 disabled:opacity-50"
                  title="Cancelar"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="text-2xl font-semibold text-[var(--color-text)]">
                ${product.price?.toFixed(2) || '0.00'}
              </div>
            )}
            <div className="text-xs text-[var(--color-text-muted)] mt-1">{product.currency_id || 'USD'}</div>
          </div>

          {/* Amazon Price - read only */}
          <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Precio Amazon</div>
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {canMoney && amazonPrice != null ? `$${Number(amazonPrice).toFixed(2)}` : '\u2014'}
            </div>
            <div className="text-xs text-[var(--color-text-muted)] mt-1">{canMoney ? 'USD' : ''}</div>
          </div>

          {/* Margin - color coded */}
          <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="flex items-center gap-1 mb-1">
              <Percent size={12} className="text-[var(--color-text-muted)]" />
              <span className="text-xs text-[var(--color-text-muted)]">Margen</span>
            </div>
            <div
              className="text-2xl font-semibold"
              style={{ color: marginColor(canMoney ? product.margin_pct : null) }}
            >
              {canMoney && product.margin_pct != null ? `${Number(product.margin_pct).toFixed(1)}%` : '\u2014'}
            </div>
            <div className="text-xs mt-1" style={{ color: marginColor(canMoney ? product.margin_pct : null) }}>
              {canMoney && product.margin_pct != null
                ? product.margin_pct >= 30 ? 'Saludable' : product.margin_pct >= 15 ? 'Moderado' : 'Bajo'
                : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Markup Configuration */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Percent size={16} />
          Configuracion de Markup
        </h3>
        <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)] space-y-3">
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                product.markup_override
                  ? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]'
                  : 'bg-[var(--color-text-muted)]/15 text-[var(--color-text-muted)]'
              }`}
            >
              {product.markup_override ? 'Override activo' : 'Regla global'}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Tipo</div>
              <div className="text-sm font-medium text-[var(--color-text)]">
                {product.markup_type === 'percentage' ? 'Porcentaje' : product.markup_type === 'fixed' ? 'Fijo' : product.markup_type || '\u2014'}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Valor</div>
              <div className="text-sm font-medium text-[var(--color-text)]">
                {canMoney && product.markup_value != null
                  ? product.markup_type === 'percentage'
                    ? `${product.markup_value}%`
                    : `$${Number(product.markup_value).toFixed(2)}`
                  : '\u2014'}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Precio Amazon (base)</div>
              <div className="text-sm font-medium text-[var(--color-text)]">
                {canMoney && product.amazon_price != null ? `$${Number(product.amazon_price).toFixed(2)}` : '\u2014'}
              </div>
            </div>
          </div>

          {!product.markup_override && (
            <p className="text-xs text-[var(--color-text-muted)] italic">
              Este item usa las reglas de markup globales del seller. Activa "Markup Override" en la tab General para personalizar.
            </p>
          )}
          {product.markup_override && (
            <p className="text-xs text-[var(--color-text-muted)] italic">
              Valores de markup son de solo lectura. Para modificarlos se requiere un endpoint dedicado (pendiente).
            </p>
          )}
        </div>
      </div>

      {/* Net Proceeds + Competencia por Site */}
      {product.site_items && product.site_items.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <DollarSign size={16} />
            Net Proceeds y Competencia por Site
            {compLoading && (
              <span className="text-xs font-normal text-[var(--color-text-muted)] ml-2">cargando\u2026</span>
            )}
          </h3>
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border-light)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-bg)] border-b border-[var(--color-border-light)]">
                  <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Site</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Net Proceeds</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Precio listing</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Sugerido ML</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Mínimo competencia</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Estado competencia</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Para ganar</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Te gana / Motivo</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Oportunidades</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">Acci\u00f3n</th>
                </tr>
              </thead>
              <tbody>
                {(product.site_items as SiteItem[]).map((si) => {
                  const comp = compBySite.get(si.site_id);
                  const badge = statusBadge(comp?.ptw_status ?? null);
                  const losing = comp?.ptw_status === 'losing' || comp?.ptw_status === 'competing';
                  const reasons = (comp?.reason || []).map((r) => REASON_LABELS[r] || r);
                  const opportunities = (comp?.boosts || [])
                    .filter((b) => b.status === 'opportunity')
                    .map((b) => BOOST_LABELS[b.id] || b.description || b.id);
                  const suggestedNet =
                    losing && comp?.price_to_win && si.net_proceeds && comp.current_price
                      ? Number(si.net_proceeds) *
                        (Number(comp.price_to_win) / Number(comp.current_price))
                      : null;

                  return (
                    <tr
                      key={si.site_item_id}
                      className="border-b border-[var(--color-border-light)] last:border-b-0 hover:bg-[var(--color-surface-hover)] align-top"
                    >
                      <td className="px-3 py-2 text-[var(--color-text)] font-medium whitespace-nowrap">
                        {si.site_id}
                      </td>
                      <td className="px-3 py-2 text-right text-[var(--color-text)] whitespace-nowrap">
                        {canMoney && si.net_proceeds != null ? `$${Number(si.net_proceeds).toFixed(2)}` : '\u2014'}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-[var(--color-text)] whitespace-nowrap">
                        ${Number(si.price).toFixed(2)} <span className="text-[var(--color-text-muted)] text-xs">{si.currency_id}</span>
                      </td>
                      {(() => {
                        const bench = benchBySite.get(si.site_id);
                        const hasData = bench && bench.status !== 'no_data';
                        const sugUsd = hasData ? bench?.suggested_price_usd ?? null : null;
                        const curUsd = bench?.current_price_usd ?? null;
                        let deltaColor = 'var(--color-text-muted)';
                        let deltaTxt: string | null = null;
                        if (sugUsd != null && curUsd != null && curUsd > 0) {
                          const diff = ((curUsd - sugUsd) / sugUsd) * 100;
                          if (diff > 0.5) {
                            deltaColor = 'var(--color-error)';
                            deltaTxt = `+${diff.toFixed(1)}%`;
                          } else if (diff < -0.5) {
                            deltaColor = 'var(--color-success)';
                            deltaTxt = `${diff.toFixed(1)}%`;
                          } else {
                            deltaColor = 'var(--color-success)';
                            deltaTxt = '≈';
                          }
                        }
                        const lowUsd = hasData ? bench?.lowest_price_usd ?? null : null;
                        return (
                          <>
                            <td
                              className="px-3 py-2 text-right whitespace-nowrap"
                              title={bench?.status ? `Status: ${bench.status}` : 'Sin datos de benchmark'}
                            >
                              {sugUsd != null ? (
                                <span className="text-[var(--color-text)]">
                                  ${sugUsd.toFixed(2)}
                                  {deltaTxt && (
                                    <span className="block text-xs" style={{ color: deltaColor }}>
                                      {deltaTxt}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-[var(--color-text-muted)]">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {lowUsd != null ? (
                                <span className="text-[var(--color-text)]">${lowUsd.toFixed(2)}</span>
                              ) : (
                                <span className="text-[var(--color-text-muted)]">—</span>
                              )}
                            </td>
                          </>
                        );
                      })()}
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ color: badge.color, background: `${badge.color}26` }}
                        >
                          {badge.emoji} {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {comp?.price_to_win ? (
                          <span className="font-medium text-[var(--color-text)]">
                            ${Number(comp.price_to_win).toFixed(2)}
                            {comp.price_gap_pct != null && (
                              <span className="text-xs text-[var(--color-text-muted)] block">
                                {Number(comp.price_gap_pct) > 0 ? '+' : ''}
                                {Number(comp.price_gap_pct).toFixed(1)}% gap
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">\u2014</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {comp?.winner_info?.item_id &&
                        comp.winner_info.item_id !== si.site_item_id ? (
                          <span className="text-[var(--color-text)]">
                            {comp.winner_info.item_id}
                            {comp.winner_info.price != null && (
                              <span className="text-[var(--color-text-muted)]"> @ ${Number(comp.winner_info.price).toFixed(2)}</span>
                            )}
                          </span>
                        ) : reasons.length > 0 ? (
                          <span className="text-[var(--color-warning)]">{reasons.join(', ')}</span>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">\u2014</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {opportunities.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {opportunities.map((label) => (
                              <span
                                key={label}
                                className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--color-warning)]/15 text-[var(--color-warning)]"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">\u2014</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        {losing && suggestedNet && suggestedNet > 0 && can('catalog.edit_price') ? (
                          <button
                            onClick={() => handleApplySuggested(si.site_id, suggestedNet)}
                            disabled={applyingSite === si.site_id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-80 disabled:opacity-50"
                            title={canMoney ? `Aplicar net proceeds sugerido $${suggestedNet.toFixed(2)} en ${si.site_id}` : `Aplicar el net proceeds sugerido en ${si.site_id}`}
                          >
                            <Zap size={12} />
                            {applyingSite === si.site_id ? 'Aplicando\u2026' : 'Igualar'}
                          </button>
                        ) : (
                          <span className="text-xs text-[var(--color-text-muted)]">\u2014</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {compRows.length === 0 && !compLoading && (
            <p className="text-xs text-[var(--color-text-muted)] italic mt-2">
              Sin datos de competencia a\u00fan. Se sincronizan cada 4hs por el motor de pricing.
            </p>
          )}
        </div>
      )}

      {/* Cost Info — bloque de puro dinero: sin la llave no se muestra */}
      {canMoney && product.cost_source && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <DollarSign size={16} />
            Informacion de Costo
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
              <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Fuente de costo</div>
              <div className="text-sm font-medium text-[var(--color-text)]">
                {product.cost_source === 'internal_stock' ? 'Stock Interno' : product.cost_source === 'amazon' ? 'Amazon' : product.cost_source}
              </div>
            </div>
            {product.internal_cost != null && (
              <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Costo interno</div>
                <div className="text-sm font-medium text-[var(--color-text)]">${Number(product.internal_cost).toFixed(2)}</div>
              </div>
            )}
            {product.supplier_name && (
              <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                <div className="text-xs text-[var(--color-text-muted)] mb-0.5">Proveedor</div>
                <div className="text-sm font-medium text-[var(--color-text)]">{product.supplier_name}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
